import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../../components/header';
import { AddFAB, FAB_BOTTOM_SPACING } from '../../components/add-fab';
import { useUsers } from '../../queries/users';
import { Input } from '../../components/input';
import { FilterIcon } from '../../assets/svgs';
import { COLORS } from '../../consts/colors';
import { shouldFilterUserByQuery } from '../../utils/users';
import { User } from '../../@types/users';
import { UsersList } from './users-list';
import { AddUserModal } from './add-user-modal';
import { EditUserModal } from './edit-user-modal';
import { FilterUsersModal, UserFilterOptions } from './filter-users-modal';
import { CounterRow, FilterRows, ResultsCounter, UsersListWrapper } from './style';

const QUERY_FILTER_KEY = 'query';
const DRIVERS_ONLY_FILTER_KEY = 'driversOnly';
const NON_DRIVERS_FILTER_KEY = 'nonDrivers';

export const UsersScreen = () => {
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [isFiltersModalVisible, setFiltersModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const { isFetching, data: users } = useUsers();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = {
    [QUERY_FILTER_KEY]: searchParams.get(QUERY_FILTER_KEY) || '',
    [DRIVERS_ONLY_FILTER_KEY]: searchParams.get(DRIVERS_ONLY_FILTER_KEY) === '1',
    [NON_DRIVERS_FILTER_KEY]: searchParams.get(NON_DRIVERS_FILTER_KEY) === '1',
  };

  const allUsers = users || [];

  const handleFilterQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    const newParams: Record<string, string> = {};
    if (value) {
      newParams[QUERY_FILTER_KEY] = value;
    }
    if (filters[DRIVERS_ONLY_FILTER_KEY]) {
      newParams[DRIVERS_ONLY_FILTER_KEY] = '1';
    }
    if (filters[NON_DRIVERS_FILTER_KEY]) {
      newParams[NON_DRIVERS_FILTER_KEY] = '1';
    }

    setSearchParams(newParams, { replace: true });
  };

  const handleFilterOptionsChange = (options: UserFilterOptions) => {
    const newParams: Record<string, string> = {};
    if (filters[QUERY_FILTER_KEY]) {
      newParams[QUERY_FILTER_KEY] = filters[QUERY_FILTER_KEY];
    }
    if (options.driversOnly) {
      newParams[DRIVERS_ONLY_FILTER_KEY] = '1';
    }
    if (options.nonDriversOnly) {
      newParams[NON_DRIVERS_FILTER_KEY] = '1';
    }

    setSearchParams(newParams, { replace: true });
    setFiltersModalVisible(false);
  };

  const filterByOptions = (user: User) => {
    if (filters[DRIVERS_ONLY_FILTER_KEY] && !user.isDriver) {
      return false;
    }
    if (filters[NON_DRIVERS_FILTER_KEY] && user.isDriver) {
      return false;
    }

    return true;
  };

  const usersList = allUsers
    .filter(filterByOptions)
    .filter((user) => shouldFilterUserByQuery(user, filters[QUERY_FILTER_KEY]));

  const isModalFilterActive =
    filters[DRIVERS_ONLY_FILTER_KEY] || filters[NON_DRIVERS_FILTER_KEY];

  return (
    <>
      <Header screenTitle="משתמשים" hasBack isLoading={isFetching} />
      <div
        style={{
          width: '95%',
          margin: '10px auto 0 auto',
          paddingBottom: FAB_BOTTOM_SPACING,
        }}
      >
        <FilterRows>
          <Input
            value={filters[QUERY_FILTER_KEY]}
            placeholder="סינון"
            onChange={handleFilterQueryChange}
          />

          <FilterIcon
            onClick={() => setFiltersModalVisible(true)}
            style={{
              height: 50,
              backgroundColor: isModalFilterActive ? COLORS.LIGHT_BLUE : COLORS.DARK_BLUE,
              padding: '0px 3px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
            fill={COLORS.WHITE}
          />
        </FilterRows>

        <CounterRow>
          <ResultsCounter>
            {`מציג ${usersList.length} מתוך ${allUsers.length}`}
          </ResultsCounter>
        </CounterRow>

        <UsersListWrapper>
          <UsersList
            users={usersList}
            onUserClick={(user) => {
              setEditingUser(user);
            }}
          />
        </UsersListWrapper>

        <AddFAB onClick={() => setIsAddUserModalVisible(true)} />
      </div>

      {isFiltersModalVisible && (
        <FilterUsersModal
          activeOptions={{
            driversOnly: filters[DRIVERS_ONLY_FILTER_KEY],
            nonDriversOnly: filters[NON_DRIVERS_FILTER_KEY],
          }}
          onCancel={() => setFiltersModalVisible(false)}
          onSaveClick={handleFilterOptionsChange}
        />
      )}

      {isAddUserModalVisible && (
        <AddUserModal onCancel={() => setIsAddUserModalVisible(false)} />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onCancel={() => setEditingUser(null)}
        />
      )}
    </>
  );
};

